/**
 * The Ask AI panel's window chrome, made reusable.
 *
 * This replaces two attempts at dragging Flux's <dialog>, and the lesson is worth keeping:
 * the dialog was the problem, not the drag. A <dialog> sits in the top layer with a backdrop
 * and Flux's open/close TRANSITIONS on it, so every transform written during a drag was
 * tweened instead of applied — the panel chased the cursor through an easing curve however
 * lean the pointer handling was. The Ask AI popup never felt like that because it never was
 * a dialog: a plain fixed <section>, moved directly, nothing animating in between.
 *
 * Two ways to open, because two kinds of caller exist:
 *
 *   - `open` may be handed a Livewire entangle, for a panel a component drives through a
 *     property (the AI-keys editor).
 *   - `name` subscribes to the same modal-show / modal-close events Flux modals use, so
 *     server code written against Flux::modal('confirm')->show() opens this panel without
 *     knowing anything changed. Listening on window catches every path — PHP-dispatched
 *     events bubble from the component element through document to window, and Flux's own
 *     client helper dispatches on document, which bubbles to window too.
 *
 * Drag mechanics are zarPanel's: pointer listeners exist only for the duration of a drag,
 * and the viewport clamp runs once on release — measuring the panel mid-drag forces a
 * layout recalculation per frame, which is the other half of what "laggy" was.
 */
export const floatingPanel = (config = {}) => ({
    open: config.open ?? false,
    x: 0,
    y: 0,
    panel: null,

    init() {
        /*
         * The panel element, captured ONCE — never read from `this.$el` inside a drag.
         *
         * Alpine's $el is bound to the element whose expression is being evaluated, and the
         * drag starts from a pointerdown on the <header>. Read during that call, $el is the
         * header — so the drag translated the header and left the body behind, the panel
         * visibly tearing in two. During init() the evaluation context is the component
         * root, which is the one moment $el reliably means the panel.
         */
        this.panel = this.$el;

        if (config.name) {
            this._show = (event) => {
                if (event.detail?.name === config.name) this.open = true;
            };
            this._close = (event) => {
                if (event.detail?.name === config.name) this.open = false;
            };
            window.addEventListener('modal-show', this._show);
            window.addEventListener('modal-close', this._close);
        }

        /*
         * Back to the middle for the next opening. A workspace panel (zarPanel) remembers
         * where you put it; a dialog is a passing interruption, and reappearing wherever it
         * was last shoved — for a reason long forgotten — reads as broken.
         */
        this.$watch('open', (value) => {
            if (value) {
                this.centre();

                return;
            }

            this.reset();

            /*
             * Tell whatever lives inside that the panel just closed.
             *
             * A searchable select keeps its own open/closed state, and a menu that was open
             * when the panel closed stays "open" in Alpine while being invisible on screen.
             * The next click on its trigger then TOGGLES it shut — so the first click after
             * reopening the panel does nothing, which reads as "the dropdown is broken".
             * This is the same two-clicks failure the morph hook fixed for re-renders; a
             * closing panel is just another way for the menu to vanish out from under its
             * own state.
             */
            window.dispatchEvent(new CustomEvent('floating-panel:closed', { detail: { panel: this.panel } }));
        });

        /*
         * Put the position back after every Livewire round trip.
         *
         * The offset lives in an inline style the server's HTML knows nothing about, so a
         * morph strips it and the panel snaps back to its CSS default — picking a provider
         * inside the editor visibly threw the panel from the content column's centre to the
         * screen's. State survives in Alpine; only the attribute is wiped; writing it again
         * is the whole fix.
         */
        this._remorphed = () => {
            if (this.open) this.apply();
        };
        window.addEventListener('zar:morphed', this._remorphed);
    },

    destroy() {
        if (config.name) {
            window.removeEventListener('modal-show', this._show);
            window.removeEventListener('modal-close', this._close);
        }
        window.removeEventListener('zar:morphed', this._remorphed);
    },

    hide() {
        this.open = false;
    },

    start(event) {
        // Buttons, inputs and anything marked data-no-drag stay interactive.
        if (event.target.closest('button, input, select, textarea, a, [data-no-drag]')) return;

        // Without this a drag across the header selects its text instead of moving the panel.
        event.preventDefault();

        const sx = event.clientX - this.x;
        const sy = event.clientY - this.y;

        const move = (moveEvent) => {
            this.x = moveEvent.clientX - sx;
            this.y = moveEvent.clientY - sy;
            this.apply();
        };

        const up = () => {
            this.clamp();
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };

        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    },

    /**
     * Never let the panel settle somewhere it cannot be grabbed back from. On RELEASE, not
     * per move — the pointer leads, and the panel settles up at the end.
     */
    clamp() {
        const box = this.panel.getBoundingClientRect();

        // A box with no size is nothing measurable to keep on screen.
        if (box.width === 0 && box.height === 0) return;

        const margin = 48;
        const left = box.left - this.x;
        const right = box.right - this.x;
        const top = box.top - this.y;

        this.x = Math.min(Math.max(this.x, margin - right), window.innerWidth - margin - left);
        this.y = Math.min(Math.max(this.y, -top), window.innerHeight - margin - top);
        this.apply();
    },

    reset() {
        this.x = 0;
        this.y = 0;
        this.apply();
    },

    /**
     * Centred over the container this panel belongs to, not over the screen.
     *
     * The CSS (inset-0 + auto margins) centres in the viewport, but on a page with a
     * sidebar the content lives in the column NEXT to it, and a viewport-centred panel
     * sits half over the navigation. The panel's parent is that column — it is rendered
     * inside it and merely positioned out of it — so the offset between the column's
     * centre and the viewport's is measured at each opening, which follows any layout,
     * any breakpoint, and any page this component is dropped into. Dragging then starts
     * from there.
     */
    centre() {
        const anchor = this.panel.parentElement;

        if (! anchor) return;

        const box = anchor.getBoundingClientRect();

        // A collapsed or unmeasurable container says nothing about where the middle is.
        if (box.width === 0) return;

        this.x = Math.round(box.left + box.width / 2 - window.innerWidth / 2);
        this.y = 0;
        this.apply();
    },

    /*
     * Written straight to the element, off Alpine's reactive path: a bound style is
     * re-evaluated on every change, and a string binding replaces the whole attribute —
     * including the `display: none` x-show wrote there, which is the zarPanel trap.
     */
    apply() {
        this.panel.style.translate = `${this.x}px ${this.y}px`;
    },
});
